<script setup lang="ts">
import { Kunde } from '@/api/model';
import type { useKundenbereichStore } from '@/stores/kundenbereichStore';
import { t } from '@/utils/i18n';
import { FilterMatchMode } from '@primevue/core/api';
import { computed, inject, onMounted, ref, type ComputedRef } from 'vue';
import { useRouter } from 'vue-router';

// Konstante für die maximale Anzahl von Filialen, bei der noch ein Dropdown-Menü angezeigt wird
// Bei mehr Filialen wird stattdessen ein Dialog verwendet
const MAX_FILIALEN_FUER_DROPDOWN = 15;

// Kunden-Parameter und Store von AppLayout erhalten
const kundeFromRoute = inject<ComputedRef<string | null>>('kunde');
const kundenbereichStore = inject<ReturnType<typeof useKundenbereichStore>>('kundenbereichStore');
const anzeigeKundennummer = ref<string>(kundeFromRoute?.value?.toUpperCase() ?? kundenbereichStore.kunde.kundennummer);

// Berechne, ob ein Dialog oder ein Dropdown angezeigt werden soll
const popupVerwenden = kundenbereichStore?.unterkunden?.length > MAX_FILIALEN_FUER_DROPDOWN;

// Referenz für das Dropdown-Menü
const dropdownMenu = ref<any>(null);

const dropdownMenuItems = ref<any>();

// Dropdown nur aufbereiten wenn es auch verwendet wird
onMounted(() => {
    if (!popupVerwenden && kundenbereichStore?.alleKunden) {
        dropdownMenuItems.value = kundenbereichStore.alleKunden.map((filiale) => ({
            label: `${filiale.kundennummer} - ${filiale.name}`,
            command: () => filialeWechseln(filiale.kundennummer),
            isCurrent: filiale.kundennummer === anzeigeKundennummer.value
        }));
    }

    // Setze die aktuelle Filiale als initiale Auswahl für die Tabelle
    if (popupVerwenden && !ausgewaehlteFiliale.value && aktuelleFiliale.value) {
        ausgewaehlteFiliale.value = aktuelleFiliale.value;
    }
});

// Funktion zum Öffnen des Dropdown-Menüs
const toggleMenu = (event: Event) => {
    if (dropdownMenu.value) {
        dropdownMenu.value.toggle(event);
    }
};

// Tabellenspalten definieren
const tabellenspalten = computed(() => [
    { field: 'kundennummer', header: t('[FilialAuswahl]KundenNr') },
    { field: 'name', header: t('[FilialAuswahl]Name') },
    { field: 'strasse', header: t('[FilialAuswahl]Strasse') },
    { field: 'plz', header: t('[FilialAuswahl]PLZ') },
    { field: 'ort', header: t('[FilialAuswahl]Ort') }
]);

// Globale Filterfelder aus den Tabellenspalten ableiten
const globalFilterFields = computed(() => tabellenspalten.value.map((spalte) => spalte.field));

// Popup
const dialogVisible = ref<boolean>(false);
const ausgewaehlteFiliale = ref<Kunde>();

// Finde die aktuelle Filiale in der Liste aller Filialen
const aktuelleFiliale = computed(() => {
    return kundenbereichStore.alleKunden.find((filiale) => filiale.kundennummer === anzeigeKundennummer.value);
});

const tabellenFilter = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
});

// Funktion zum Öffnen des Dialogs
const dialogOeffnen = () => {
    dialogVisible.value = true;
};

// Funktion zum Abbrechen der Filialauswahl
const abbrechen = () => {
    dialogVisible.value = false;
};

// Router für die Navigation verwenden
const router = useRouter();

// Wechsel der Filiale wird mit einem kompletten Page-Reload realisiert
// Dadurch ist sichergestellt, dass die Komponeten alle in einem konsistenten Zustand sind.
// Anderenfalls müssten alle Komponenten Änderungen Kunden-Parameter in der Url überwachen.
const filialeWechseln = (kundennummer: string) => {
    // Generiere die URL mit dem Vue Router, um die Basis-URL zu berücksichtigen
    const url = router.resolve({
        name: 'dashboard',
        params: { kunde: kundennummer.toLowerCase() }
    }).href;

    // Vollständigen Page-Reload durchführen
    window.location.href = url;
};
</script>

<template>
    <div class="flex items-center" v-if="kundenbereichStore?.unterkunden?.length > 0">
        <!-- Dropdown-Button für wenige Filialen -->
        <template v-if="!popupVerwenden">
            <Menu ref="dropdownMenu" :model="dropdownMenuItems" :popup="true">
                <template #item="{ item, props }">
                    <a class="flex items-center p-2 hover:bg-surface-100 dark:hover:bg-surface-700" v-bind="props.action" :class="{ 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-100 font-semibold': item.isCurrent }">
                        <span>{{ item.label }}</span>
                    </a>
                </template>
            </Menu>
            <Button class="btn-filiale" icon="pi pi-building" :label="anzeigeKundennummer" text @click="toggleMenu" v-tooltip.top="t('[FilialAuswahl]FilialeWechseln')" />
        </template>

        <!-- Button für Dialog bei vielen Filialen -->
        <template v-else>
            <Button class="btn-filiale" icon="pi pi-building" :label="anzeigeKundennummer" text @click="dialogOeffnen" v-tooltip.top="t('[FilialAuswahl]FilialeWechseln')" />
        </template>

        <!-- Dialog für Filialauswahl bei vielen Filialen -->
        <Dialog v-if="popupVerwenden" v-model:visible="dialogVisible" :header="t('[FilialAuswahl]FilialeAuswaehlen')" :modal="true" :breakpoints="{ '960px': '75vw' }" :style="{ width: '50vw' }">
            <template #header>
                <div class="flex justify-end w-full">
                    <IconField>
                        <InputIcon>
                            <i class="pi pi-search"></i>
                        </InputIcon>
                        <InputText v-model="tabellenFilter['global'].value" :placeholder="t('[FilialAuswahl]FilialeSuchen')" />
                    </IconField>
                </div>
            </template>

            <DataTable
                :value="kundenbereichStore?.alleKunden"
                v-model:filters="tabellenFilter"
                :globalFilterFields="globalFilterFields"
                scrollable
                scroll-height="600px"
                selectionMode="single"
                :virtualScrollerOptions="{ itemSize: 50 }"
                v-model:selection="ausgewaehlteFiliale"
                class="mt-3"
            >
                <Column v-for="spalte in tabellenspalten" :key="spalte.field" :field="spalte.field" :header="spalte.header" sortable> </Column>
            </DataTable>

            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button :label="t('[FilialAuswahl]Abbrechen')" icon="pi pi-times" @click="abbrechen" class="p-button-text" />
                    <Button :label="t('[FilialAuswahl]Auswaehlen')" icon="pi pi-check" @click="filialeWechseln(ausgewaehlteFiliale?.kundennummer)" :disabled="!ausgewaehlteFiliale" />
                </div>
            </template>
        </Dialog>
    </div>
</template>

<style scoped lang="css">
/* Kundennummer auf sehr kleinen Screens ausblenden */
.btn-filiale :deep(.p-button-label) {
    display: none;
}

@media (min-width: 420px) {
    .btn-filiale :deep(.p-button-label) {
        display: inline;
    }
}
</style>
